import React, { memo, useCallback, useEffect, useRef, useState } from 'react';
import { ImageOff } from 'lucide-react';
import { useStoreApi } from '@xyflow/react';
import { SCALE_LABELS, clampElevation, clampScaleIndex, shortestAngleDelta, snapScaleIndex } from './anglePresets';

interface MultiAngleViewportProps {
  imageUrl?: string;
  azimuth: number;
  elevation: number;
  scaleIndex: number;
  onAzimuthChange: (delta: number) => void;
  onElevationChange: (delta: number) => void;
  onScaleIndexChange: (index: number) => void;
}

const GREEN = 0x2dd4a0;
const PINK = 0xec5fa1;
const ORANGE = 0xf59e0b;
const BG = 0x0a0a0b;
const RING_HEIGHT = 0.18;
const RING_RADIUS = 2.3;
// 远近轨道半径：橙线滑轨两端（特写/远景），远景端必须小于绿环半径（橙线恒在绿环内）；中景=中点=正常视角
const R_NEAR = 1.2;
const R_FAR = 2.16;
// 远近滑轨高度：固定在正前方，比绿环略高
const SCALE_LINE_Y = 0.26;
// 素材显示尺寸：方形基面（宽度随比例缩放），基准 1.5 高（0.75 的 2 倍），对齐参考图素材占比
const PLANE_HEIGHT = 1.5;
const PLANE_BASE_WIDTH = 1.5;
// 板厚：侧视/背视可辨识（照片板效果，对齐打灯视口）
const PLANE_THICKNESS = 0.07;
// 已加载图片缓存（imageUrl → HTMLImageElement）：面板重开/换回同图命中缓存时同步建纹理直接上屏，
// 避免「灰板一帧 → 贴图缓动」造成的打开闪烁；超出容量按插入序淘汰
const imageCache = new Map<string, HTMLImageElement>();
const IMAGE_CACHE_MAX = 12;
// 垂直倾斜弧半径与静止方位（立于绿环左前方，同参考图）与弧所在竖直平面的横向偏移（主体左前方）
const EL_RADIUS = 1.1;
const EL_ARC_AZIMUTH = -Math.PI / 4;
// 粉弧俯仰范围：与垂直俯仰滑块一致（-30°~60°，正值=俯拍）
const EL_ARC_MIN = -30;
const EL_ARC_MAX = 60;
const ARC_X = -0.7;
// 粉弧所在竖直平面的纵深：位于 EL_ARC_AZIMUTH 方位射线上（z = ARC_X / tan(方位角)）
const ARC_Z = ARC_X / Math.tan(EL_ARC_AZIMUTH);
// 控制点屏幕命中容差与点本体命中下限（px）
const CLICK_TOLERANCE_PX = 30;
const MARKER_BODY_HIT_PX = 12;
// 渲染像素比上限：防高倍反向缩放 + 高分屏叠加时背衬尺寸过大
const MAX_PIXEL_RATIO = 4;
// 画布净拉伸倍数 = 实测视觉宽 / 布局宽：InverseScaleBox 以 scale(1/zoom) 反向缩放面板保持固定尺寸，
// zoom<1 时画布被 CSS 放大，背衬分辨率需按倍数补足，否则贴图被拉糊（rect/offset 实测，覆盖任意变换组合）
const measureStretch = (el: HTMLElement): number => {
  const layout = el.offsetWidth;
  if (layout <= 0) return 1;
  const stretched = el.getBoundingClientRect().width;
  if (stretched <= 0) return 1;
  return stretched / layout;
};
// 渲染像素比下限：低分屏（dpr≈1~1.25）下按 2× 超采样渲染，否则 2K 贴图与画中画照片只有几百个真实像素，必然发糊
const MIN_PIXEL_RATIO = 2;
// 目标渲染像素比 = 设备像素比 × 画布实测拉伸倍数，夹在 [下限, 上限]；初始化 / 缩放订阅 / 尺寸变化三处共用
const targetPixelRatio = (el: HTMLElement): number =>
  Math.min(Math.max((window.devicePixelRatio || 1) * measureStretch(el), MIN_PIXEL_RATIO), MAX_PIXEL_RATIO);
// 主体中心（相机模型/视线/PiP 相机的注视点）：固定高度，素材平面中心与其对齐；
// 平面放大 2 倍后中心等比抬高，底边离地悬浮高度保持 0.225 不变（0.975 − 1.5/2 = 0.6 − 0.75/2）
const SUBJECT_Y = 0.975;
// 右上角「相机视角」画中画卡片：宽占视口比例、距视口边比例（渲染 setViewport 与 CSS 覆盖层共用同源常量）
const PIP_WIDTH_RATIO = 0.256;
const PIP_INSET_RATIO = 0.03;
// 卡片内四周留白（占卡片宽比例）；CSS 的百分比 padding 相对视口宽，故取 PIP_WIDTH_RATIO × PIP_PAD_RATIO
const PIP_PAD_RATIO = 0.1;
// 图片区宽高比与渲染背景色（背景与 CSS 卡片底色一致，缝隙处无接缝）
const PIP_ASPECT = 1.5;
const PIP_BG = 0x1b1b1e;
// 右键环绕灵敏度（弧度/像素）、俯仰限位与惯性衰减（每帧阻尼系数/停止阈值）
const ORBIT_SENSITIVITY = 0.005;
const ORBIT_PHI_MIN = 0.05;
const ORBIT_PHI_MAX = 1.4;
const ORBIT_DAMPING = 0.9;
const ORBIT_INERTIA_MIN = 1e-4;

const lerp = (a: number, b: number, t: number) => a + (b - a) * t;
const deg2rad = (d: number) => (d * Math.PI) / 180;
const easeOutCubic = (t: number) => 1 - Math.pow(1 - t, 3);
const MARKER_ANIM_DURATION = 320;

export const MultiAngleViewport = memo<MultiAngleViewportProps>(({
  imageUrl,
  azimuth,
  elevation,
  scaleIndex,
  onAzimuthChange,
  onElevationChange,
  onScaleIndexChange,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const canvasHostRef = useRef<HTMLDivElement>(null);
  const rendererRef = useRef<import('three').WebGLRenderer | null>(null);
  const sceneRef = useRef<import('three').Scene | null>(null);
  const cameraRef = useRef<import('three').PerspectiveCamera | null>(null);
  const greenMarkerRef = useRef<import('three').Mesh | null>(null);
  const pinkMarkerRef = useRef<import('three').Mesh | null>(null);
  const orangeMarkerRef = useRef<import('three').Mesh | null>(null);
  // 复用的三维向量（控制点屏幕投影命中判定用）
  const scratchVecRef = useRef<import('three').Vector3 | null>(null);
  const planeRef = useRef<import('three').Mesh | null>(null);
  // 当前贴图（跨 imageUrl 变更持有：换图时旧贴图保持上屏直到新图就绪，避免灰闪）
  const loadedTextureRef = useRef<import('three').Texture | null>(null);
  // 当前贴图对应的图片 URL：建板时已同步贴图的同 URL 不重复应用（贴图 effect 直接跳过）
  const loadedTextureUrlRef = useRef<string | null>(null);
  const cameraModelRef = useRef<import('three').Group | null>(null);
  const viewLineRef = useRef<import('three').Line | null>(null);
  // 橙点垂线（相机位到地面投影，方向始终竖直）
  const dropLineRef = useRef<import('three').Line | null>(null);
  const pipCameraRef = useRef<import('three').PerspectiveCamera | null>(null);
  const sizeRef = useRef({ width: 330, height: 330 });
  // ReactFlow store：画布缩放订阅用（Hook 只能在组件顶层调用，不能放进 effect）
  const store = useStoreApi();
  // 观察相机轨道状态（右键拖动）：初始位姿对应 (0, 1.69, 5.11) 看向主体中心
  const orbitStateRef = useRef({ theta: 0, phi: deg2rad(12), dist: 5.22 });
  const orbitModeRef = useRef(false);
  // 右键轨道惯性：释放时的角速度（弧度/帧）与惯性动画 rAF id
  const orbitVelRef = useRef({ theta: 0, phi: 0 });
  const inertiaRafRef = useRef(0);
  const azimuthRef = useRef(azimuth);
  const elevationRef = useRef(elevation);
  const scaleIndexRef = useRef(scaleIndex);
  const draggingRef = useRef(false);
  // 当前拖拽的控制轴（左键命中控制点后才设置）
  const dragTargetRef = useRef<'azimuth' | 'elevation' | 'scale' | null>(null);
  // 绿点拖拽期间最近一次下发的目标方位角（度）：以它为基准算增量，两次 move 间父级未重渲染也不会累积误差
  const azDragAngleRef = useRef(0);
  // 景别拖拽期间的连续浮点档位（0~4）：本地插值橙点与相机距离，跨整数档才下发
  const scaleDragFloatRef = useRef(0);
  // 俯仰拖拽期间的连续浮点仰角（度）与最近一次上报的整数仰角：本地插值粉点，跨整数度才上报差值
  const elDragFloatRef = useRef(0);
  const elDragSentRef = useRef(0);
  const rafRef = useRef(0);
  const animRafRef = useRef(0);
  const displayRef = useRef({ azimuth, elevation, scaleIndex });
  const dragPosRef = useRef<{ x: number; y: number } | null>(null);
  const [ready, setReady] = useState(false);
  const [textureFailed, setTextureFailed] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  // 悬停在线条圆点（方位/俯仰/景别标记）上时显示手掌；未按下且未悬停时保持默认箭头
  const [hoverMarker, setHoverMarker] = useState(false);

  azimuthRef.current = azimuth;
  elevationRef.current = elevation;
  scaleIndexRef.current = scaleIndex;

  // 主视图 + 右上角「相机视角」画中画双通道渲染（scissor 限制清屏与绘制范围）
  const renderScene = useCallback(() => {
    const renderer = rendererRef.current;
    const scene = sceneRef.current;
    const camera = cameraRef.current;
    const pipCamera = pipCameraRef.current;
    if (!renderer || !scene || !camera) return;
    const { width, height } = sizeRef.current;
    renderer.setScissorTest(false);
    renderer.setViewport(0, 0, width, height);
    renderer.render(scene, camera);
    if (pipCamera) {
      // 深灰卡片由 CSS 绘制，canvas 只渲染卡片内图片矩形：四周留白 PIP_PAD_RATIO，GL 的 y 轴向上需从顶部换算
      const cardW = Math.round(width * PIP_WIDTH_RATIO);
      const pad = Math.round(cardW * PIP_PAD_RATIO);
      const imgW = cardW - pad * 2;
      const imgH = Math.round(imgW / PIP_ASPECT);
      const px = width - cardW - Math.round(width * PIP_INSET_RATIO) + pad;
      const py = height - Math.round(height * PIP_INSET_RATIO) - pad - imgH;
      renderer.setClearColor(PIP_BG);
      renderer.setScissorTest(true);
      // 以卡片底色清空图片矩形外扩 1px：覆盖 CSS 镂空的亚像素缝隙与描边抗锯齿（不铺满整卡，避免直角溢出卡片圆角外）
      renderer.setScissor(px - 1, py - 1, imgW + 2, imgH + 2);
      renderer.clear(true, false, false);
      renderer.setViewport(px, py, imgW, imgH);
      renderer.setScissor(px, py, imgW, imgH);
      renderer.render(scene, pipCamera);
      renderer.setScissorTest(false);
      renderer.setViewport(0, 0, width, height);
      renderer.setClearColor(BG);
    }
  }, []);

  const requestRender = useCallback(() => {
    cancelAnimationFrame(rafRef.current);
    rafRef.current = requestAnimationFrame(renderScene);
  }, [renderScene]);

  // 右键轨道：围绕主体中心更新观察相机位姿
  const updateOrbitCamera = useCallback(() => {
    const camera = cameraRef.current;
    if (!camera) return;
    const { theta, phi, dist } = orbitStateRef.current;
    const r = dist * Math.cos(phi);
    camera.position.set(r * Math.sin(theta), SUBJECT_Y + dist * Math.sin(phi), r * Math.cos(theta));
    camera.lookAt(0, SUBJECT_Y, 0);
  }, []);

  // PiP fov：以固定中景距离为基准做完整取景（垂直张角取「高度需求」与「宽度需求÷视口宽高比」较大者，防裁切）
  // 与实时景别距离解耦：景别切换只移动相机不改 fov → 近大远小可见
  const updatePipFov = useCallback(() => {
    const pip = pipCameraRef.current;
    if (!pip) return;
    const halfW = ((PLANE_BASE_WIDTH / 2) * (planeRef.current?.scale.x ?? 1)) / PIP_ASPECT;
    const half = Math.max(PLANE_HEIGHT / 2, halfW);
    pip.fov = (2 * Math.atan(half / lerp(R_NEAR, R_FAR, 0.5)) * 180) / Math.PI;
    pip.updateProjectionMatrix();
  }, []);

  // 按给定参数直设标记位置（si 用无取整 clamp，保证景别插值时距离连续）
  // 三轴线静止、控制点分离滑动：绿点绕环（相机跟随其方位），粉点沿静止弧，橙点沿静止滑轨（相机距离跟随其位置）
  const applyMarkerPositions = useCallback((az: number, el: number, si: number) => {
    const azRad = deg2rad(az);
    const elRad = deg2rad(el);
    const siClamped = Math.max(0, Math.min(4, si));
    const camDist = lerp(R_NEAR, R_FAR, 1 - siClamped / 4);
    const horizontal = camDist * Math.cos(elRad);
    const camX = horizontal * Math.sin(azRad);
    const camY = SUBJECT_Y + camDist * Math.sin(elRad);
    const camZ = horizontal * Math.cos(azRad);
    if (greenMarkerRef.current) {
      greenMarkerRef.current.position.set(
        RING_RADIUS * Math.sin(azRad),
        RING_HEIGHT,
        RING_RADIUS * Math.cos(azRad)
      );
    }
    if (pinkMarkerRef.current) {
      // 粉点沿静止弧滑动：弧心 (ARC_X, SUBJECT_Y, ARC_Z)，鼓出方向 EL_ARC_AZIMUTH，仰角即弧上角
      const dirX = Math.sin(EL_ARC_AZIMUTH);
      const dirZ = Math.cos(EL_ARC_AZIMUTH);
      pinkMarkerRef.current.position.set(
        ARC_X + EL_RADIUS * Math.cos(elRad) * dirX,
        SUBJECT_Y + EL_RADIUS * Math.sin(elRad),
        ARC_Z + EL_RADIUS * Math.cos(elRad) * dirZ
      );
    }
    if (orangeMarkerRef.current) {
      // 橙点沿静止滑轨滑动（不与相机模型重合）：滑轨固定在正前方指向图片，点的距离=相机距离
      orangeMarkerRef.current.position.set(0, SCALE_LINE_Y, camDist);
    }
    if (dropLineRef.current) {
      const dropPos = dropLineRef.current.geometry.getAttribute('position');
      dropPos.setXYZ(0, camX, 0, camZ);
      dropPos.setXYZ(1, camX, camY, camZ);
      dropPos.needsUpdate = true;
    }
    if (cameraModelRef.current) {
      cameraModelRef.current.position.set(camX, camY, camZ);
      cameraModelRef.current.lookAt(0, SUBJECT_Y, 0);
    }
    if (viewLineRef.current) {
      const position = viewLineRef.current.geometry.getAttribute('position');
      position.setXYZ(0, camX, camY, camZ);
      position.setXYZ(1, 0, SUBJECT_Y, 0);
      position.needsUpdate = true;
    }
    if (pipCameraRef.current) {
      pipCameraRef.current.position.set(camX, camY, camZ);
      pipCameraRef.current.lookAt(0, SUBJECT_Y, 0);
      updatePipFov();
    }
  }, [updatePipFov]);

  const cancelMarkerAnimation = useCallback(() => {
    if (animRafRef.current) {
      cancelAnimationFrame(animRafRef.current);
      animRafRef.current = 0;
    }
  }, []);

  // 预设/按钮/滑块/滚轮触发的过渡动画：从当前显示值插值到目标值（azimuth 走最短路径）
  const startMarkerAnimation = useCallback(
    (toAz: number, toEl: number, toSi: number) => {
      cancelMarkerAnimation();
      const from = { ...displayRef.current };
      const deltaAz = shortestAngleDelta(from.azimuth, toAz);
      const start = performance.now();
      const step = () => {
        const t = Math.min(1, (performance.now() - start) / MARKER_ANIM_DURATION);
        const k = easeOutCubic(t);
        const az = from.azimuth + deltaAz * k;
        const el = lerp(from.elevation, toEl, k);
        const si = lerp(from.scaleIndex, toSi, k);
        displayRef.current = { azimuth: az, elevation: el, scaleIndex: si };
        applyMarkerPositions(az, el, si);
        renderScene();
        if (t < 1) {
          animRafRef.current = requestAnimationFrame(step);
        } else {
          displayRef.current = { azimuth: toAz, elevation: toEl, scaleIndex: toSi };
          animRafRef.current = 0;
        }
      };
      animRafRef.current = requestAnimationFrame(step);
    },
    [applyMarkerPositions, cancelMarkerAnimation, renderScene]
  );

  // 三维场景初始化（three 动态加载，面板首开时才引入）
  useEffect(() => {
    let cancelled = false;
    const container = containerRef.current;
    const canvasHost = canvasHostRef.current;
    if (!container || !canvasHost) return;

    (async () => {
      const {
        BoxGeometry,
        BufferGeometry,
        CylinderGeometry,
        DoubleSide,
        Float32BufferAttribute,
        GridHelper,
        Group,
        Line,
        LineBasicMaterial,
        Mesh,
        MeshBasicMaterial,
        PerspectiveCamera,
        PlaneGeometry,
        Scene,
        SphereGeometry,
        Texture,
        TorusGeometry,
        Vector3,
        WebGLRenderer,
      } = await import('three');
      if (cancelled) return;

      const width = container.clientWidth || 330;
      const height = container.clientHeight || 330;
      sizeRef.current = { width, height };

      const renderer = new WebGLRenderer({ antialias: true });
      renderer.setPixelRatio(targetPixelRatio(container));
      renderer.setSize(width, height);
      renderer.setClearColor(BG);
      renderer.domElement.style.display = 'block';
      canvasHost.appendChild(renderer.domElement);

      const scene = new Scene();
      const camera = new PerspectiveCamera(45, width / height, 0.1, 100);
      // 默认水平 0°、垂直 0° 时正视主体；与 orbitStateRef 初始位姿一致（右键复位基准），拉近 15% 增强临场感
      // 位姿由 orbitState { phi: 12°, dist: 5.22 } 解算：y = SUBJECT_Y + dist·sin(phi) ≈ 2.06
      camera.position.set(0, 2.06, 5.11);
      camera.lookAt(0, SUBJECT_Y, 0);
      // 主视图需看到 layer 1（相机模型），画中画相机只看 layer 0 → 避免相机模型自身挡住 PiP 视线
      camera.layers.enable(1);
      // 素材平面在 layer 2：主视图可见；画中画相机只看 layer 2（PiP 仅呈现图片旋转）
      camera.layers.enable(2);

      const grid = new GridHelper(6, 24, 0x232326, 0x1a1a1d);

      // 图片板：带厚度盒子（背光灯箱效果，对齐打灯视口）——正面全亮贴图，背面同图压暗（透光感），侧面亮边
      // BoxGeometry 材质数组顺序 [+x, -x, +y, -y, +z(正面), -z(背面)]
      const sideMat = new MeshBasicMaterial({ color: 0x26262a });
      const backMat = new MeshBasicMaterial({ color: 0x262629 });
      const frontMat = new MeshBasicMaterial({ color: 0x2c2c2e, side: DoubleSide });
      const plane = new Mesh(
        new BoxGeometry(PLANE_BASE_WIDTH, PLANE_HEIGHT, PLANE_THICKNESS),
        [sideMat, sideMat, sideMat, sideMat, frontMat, backMat]
      );
      plane.position.y = SUBJECT_Y;
      plane.layers.set(2);

      const ring = new Mesh(
        new TorusGeometry(RING_RADIUS, 0.013, 8, 96),
        new MeshBasicMaterial({ color: GREEN })
      );
      ring.rotation.x = Math.PI / 2;
      ring.position.y = RING_HEIGHT;

      // 控制点尺寸对齐参考图（约占视口宽 3% 的可抓取球体，明显大于线宽）
      const greenMarker = new Mesh(
        new SphereGeometry(0.095, 16, 16),
        new MeshBasicMaterial({ color: GREEN })
      );

      // 静止垂直倾斜弧：立于绿环左前方（EL_ARC_AZIMUTH 方位），弧心 (ARC_X, SUBJECT_Y, ARC_Z)，
      // 仰角即弧上角，EL_ARC_MIN（-90° 正下方仰拍）→ EL_ARC_MAX（90° 正上方俯拍）；轴线世界系固定，不随方位旋转（同参考图）
      const arcPoints: import('three').Vector3[] = [];
      for (let i = 0; i <= 32; i++) {
        const a = deg2rad(EL_ARC_MIN + ((EL_ARC_MAX - EL_ARC_MIN) * i) / 32);
        arcPoints.push(
          new Vector3(
            ARC_X + EL_RADIUS * Math.cos(a) * Math.sin(EL_ARC_AZIMUTH),
            SUBJECT_Y + EL_RADIUS * Math.sin(a),
            ARC_Z + EL_RADIUS * Math.cos(a) * Math.cos(EL_ARC_AZIMUTH)
          )
        );
      }
      const elevationArc = new Line(
        new BufferGeometry().setFromPoints(arcPoints),
        new LineBasicMaterial({ color: PINK, transparent: true, opacity: 0.7 })
      );

      const pinkMarker = new Mesh(
        new SphereGeometry(0.085, 16, 16),
        new MeshBasicMaterial({ color: PINK })
      );
      const orangeMarker = new Mesh(
        new SphereGeometry(0.08, 16, 16),
        new MeshBasicMaterial({ color: ORANGE })
      );

      // 虚拟相机模型（+Z 朝向镜头；lookAt 后 +Z 指向主体中心）
      const cameraModel = new Group();
      const body = new Mesh(new BoxGeometry(0.15, 0.095, 0.2), new MeshBasicMaterial({ color: 0xe4e4e7 }));
      const lensBarrel = new Mesh(
        new CylinderGeometry(0.045, 0.05, 0.09, 20),
        new MeshBasicMaterial({ color: 0x3f3f46 })
      );
      lensBarrel.rotation.x = Math.PI / 2;
      lensBarrel.position.z = 0.14;
      const lensGlass = new Mesh(
        new CylinderGeometry(0.03, 0.03, 0.012, 20),
        new MeshBasicMaterial({ color: GREEN })
      );
      lensGlass.rotation.x = Math.PI / 2;
      lensGlass.position.z = 0.186;
      const prism = new Mesh(new BoxGeometry(0.05, 0.035, 0.07), new MeshBasicMaterial({ color: 0xd4d4d8 }));
      prism.position.set(0, 0.065, -0.02);
      cameraModel.add(body, lensBarrel, lensGlass, prism);
      // 相机模型整体移到 layer 1：主相机可见，PiP 相机（仅 layer 0）不渲染它
      cameraModel.traverse(obj => { obj.layers.set(1); });

      // 相机 → 主体中心的视线（长度即「远近距离」），顶点位随参数更新
      const viewLineGeometry = new BufferGeometry();
      viewLineGeometry.setAttribute('position', new Float32BufferAttribute(new Float32Array(6), 3));
      const viewLine = new Line(
        viewLineGeometry,
        new LineBasicMaterial({ color: ORANGE, transparent: true, opacity: 0.45 })
      );
      viewLine.frustumCulled = false;

      // 橙点垂线：相机位到地面投影（方向始终竖直，端点随参数更新）
      const dropLineGeometry = new BufferGeometry();
      dropLineGeometry.setAttribute('position', new Float32BufferAttribute(new Float32Array(6), 3));
      const dropLine = new Line(
        dropLineGeometry,
        new LineBasicMaterial({ color: ORANGE, transparent: true, opacity: 0.45 })
      );
      dropLine.frustumCulled = false;

      // 远近滑轨线：同粉弧一样固定不动，位于正前方（+Z 轴）正中指向图片，
      // 高度比绿环略高，两端=特写/远景半径，恒在绿环内
      const scaleLine = new Line(
        new BufferGeometry().setFromPoints([
          new Vector3(0, SCALE_LINE_Y, R_NEAR),
          new Vector3(0, SCALE_LINE_Y, R_FAR),
        ]),
        new LineBasicMaterial({ color: ORANGE, transparent: true, opacity: 0.7 })
      );

      // 画中画相机：位于虚拟相机位，呈现「相机所见的实时画面」（fov 使默认中景正好完整呈现画面）
      const pipCamera = new PerspectiveCamera(39, PIP_ASPECT, 0.05, 50);
      // 画中画只渲染素材平面（layer 2）：纯灰背景上的图片旋转视图
      pipCamera.layers.set(2);

      scene.add(grid, plane, ring, greenMarker, elevationArc, pinkMarker, orangeMarker, cameraModel, viewLine, dropLine, scaleLine);

      rendererRef.current = renderer;
      sceneRef.current = scene;
      cameraRef.current = camera;
      greenMarkerRef.current = greenMarker;
      pinkMarkerRef.current = pinkMarker;
      orangeMarkerRef.current = orangeMarker;
      dropLineRef.current = dropLine;
      scratchVecRef.current = new Vector3();
      planeRef.current = plane;
      cameraModelRef.current = cameraModel;
      viewLineRef.current = viewLine;
      pipCameraRef.current = pipCamera;

      // 面板重开/换回看过的图：建板时同步命中缓存直接贴图，首帧即成品
      // （场景初始化末尾会同步 renderScene 画出第一帧；若等 ready 后的贴图 effect 异步上屏，灰板会先闪现）
      const cachedImage = imageUrl ? imageCache.get(imageUrl) : undefined;
      if (cachedImage && cachedImage.complete && cachedImage.naturalWidth > 0) {
        const texture = new Texture(cachedImage);
        texture.colorSpace = 'srgb';
        texture.anisotropy = renderer.capabilities.getMaxAnisotropy();
        texture.needsUpdate = true;
        frontMat.map = texture;
        frontMat.color.set(0xffffff);
        backMat.map = texture;
        backMat.color.set(0x71717a);
        sideMat.map = texture;
        sideMat.color.set(0x909098);
        loadedTextureRef.current = texture;
        loadedTextureUrlRef.current = imageUrl ?? null;
        // 板宽按图片比例展开（clamp 0.5~2.2，对齐网络加载路径），PiP 取景同步
        plane.scale.set(
          Math.max(0.5, Math.min(2.2, cachedImage.naturalWidth / cachedImage.naturalHeight)),
          1,
          1
        );
        updatePipFov();
      }

      setReady(true);
      renderScene();
    })();

    return () => {
      cancelled = true;
      cancelAnimationFrame(rafRef.current);
      cancelAnimationFrame(animRafRef.current);
      cancelAnimationFrame(inertiaRafRef.current);
      const renderer = rendererRef.current;
      const scene = sceneRef.current;
      if (scene) {
        scene.traverse((obj) => {
          const mesh = obj as import('three').Mesh;
          if (mesh.geometry) mesh.geometry.dispose();
          const material = mesh.material as import('three').Material | import('three').Material[] | undefined;
          if (Array.isArray(material)) {
            material.forEach((m) => m.dispose());
          } else if (material) {
            const mapped = material as import('three').MeshBasicMaterial;
            if (mapped.map) mapped.map.dispose();
            material.dispose();
          }
        });
      }
      if (renderer) {
        renderer.dispose();
        renderer.forceContextLoss();
        renderer.domElement.remove();
      }
      rendererRef.current = null;
      sceneRef.current = null;
      cameraRef.current = null;
      // 材质数组（图片板六材质）走 traverse 数组分支，不会释放 map，这里显式释放贴图
      loadedTextureRef.current?.dispose();
      loadedTextureRef.current = null;
      greenMarkerRef.current = null;
      pinkMarkerRef.current = null;
      orangeMarkerRef.current = null;
      dropLineRef.current = null;
      scratchVecRef.current = null;
      planeRef.current = null;
      cameraModelRef.current = null;
      viewLineRef.current = null;
      pipCameraRef.current = null;
    };
  }, []);

  // 参数 → 轨道标记位置同步：拖拽期间直设跟手；其余变化（预设/按钮/滑块/滚轮）带过渡动画
  useEffect(() => {
    if (!ready) return;
    if (draggingRef.current) {
      cancelMarkerAnimation();
      displayRef.current = { azimuth, elevation, scaleIndex };
      applyMarkerPositions(azimuth, elevation, scaleIndex);
      requestRender();
      return;
    }
    startMarkerAnimation(azimuth, elevation, scaleIndex);
  }, [ready, azimuth, elevation, scaleIndex, applyMarkerPositions, cancelMarkerAnimation, startMarkerAnimation, requestRender]);

  // 图片纹理（跨域等加载失败时降级占位灰面，不影响生成）
  // 换图策略对齐打灯视口：旧贴图保持上屏直到新图就绪，就绪后三面颜色与板宽 300ms smoothstep 缓动，
  // 首帧「贴图×起点色≈占位灰」衔接无跳变
  useEffect(() => {
    if (!ready) return;
    const plane = planeRef.current;
    if (!plane) return;
    const mats = plane.material as import('three').MeshBasicMaterial[];
    const front = mats[4];
    const back = mats[5];
    const side = mats[0];
    let disposed = false;
    let boardRafId = 0;

    setTextureFailed(false);

    /** 三面同步贴图：正面全亮 / 背面压暗透光 / 侧面亮边（颜色乘法压暗贴图） */
    const applyBoardTexture = (texture: import('three').Texture | null) => {
      front.map = texture;
      front.color.set(texture ? 0xffffff : 0x2c2c2e);
      back.map = texture;
      back.color.set(texture ? 0x71717a : 0x262629);
      side.map = texture;
      side.color.set(texture ? 0x909098 : 0x26262a);
      for (const m of [front, back, side]) m.needsUpdate = true;
    };

    if (!imageUrl) {
      applyBoardTexture(null);
      plane.scale.set(1, 1, 1);
      loadedTextureUrlRef.current = null;
      updatePipFov();
      requestRender();
      return;
    }

    /** 贴图就绪过渡：三面颜色与板宽从当前值缓动到贴图态 */
    const startBoardTransition = (texture: import('three').Texture, aspect: number) => {
      const fromFront = front.color.clone();
      const fromBack = back.color.clone();
      const fromSide = side.color.clone();
      const fromScale = plane.scale.x;
      applyBoardTexture(texture);
      const toFront = front.color.clone();
      const toBack = back.color.clone();
      const toSide = side.color.clone();
      const start = performance.now();
      const tick = (now: number) => {
        if (disposed || planeRef.current !== plane) return;
        const t = Math.min(1, (now - start) / 300);
        const e = t * t * (3 - 2 * t);
        plane.scale.set(fromScale + (aspect - fromScale) * e, 1, 1);
        front.color.copy(fromFront).lerp(toFront, e);
        back.color.copy(fromBack).lerp(toBack, e);
        side.color.copy(fromSide).lerp(toSide, e);
        // PiP 取景跟随板宽（fov 由 scale.x 解算），过渡全程画中画完整呈现图片
        updatePipFov();
        requestRender();
        if (t < 1) boardRafId = requestAnimationFrame(tick);
      };
      boardRafId = requestAnimationFrame(tick);
    };

    // 建板时已同步贴过同图（缓存命中初始化）：无需重复应用
    if (loadedTextureUrlRef.current === imageUrl) return;

    // 图片切换命中缓存（换回看过的图）：同步建纹理直接上屏，跳过灰板帧与缓动
    const cachedImage = imageCache.get(imageUrl);
    if (cachedImage && cachedImage.complete && cachedImage.naturalWidth > 0) {
      // 刷新插入序（Map 按插入序淘汰，删后重设等效 LRU 触碰）
      imageCache.delete(imageUrl);
      imageCache.set(imageUrl, cachedImage);
      import('three').then(({ Texture }) => {
        if (disposed || planeRef.current !== plane) return;
        const texture = new Texture(cachedImage);
        texture.colorSpace = 'srgb';
        texture.anisotropy = rendererRef.current?.capabilities.getMaxAnisotropy() ?? 1;
        texture.needsUpdate = true;
        loadedTextureRef.current?.dispose();
        loadedTextureRef.current = texture;
        loadedTextureUrlRef.current = imageUrl;
        applyBoardTexture(texture);
        plane.scale.set(
          Math.max(0.5, Math.min(2.2, cachedImage.naturalWidth / cachedImage.naturalHeight)),
          1,
          1
        );
        updatePipFov();
        requestRender();
      });
      return () => {
        disposed = true;
        cancelAnimationFrame(boardRafId);
      };
    }

    import('three').then(({ TextureLoader }) => {
      if (disposed) return;
      new TextureLoader().load(
        imageUrl,
        (texture) => {
          if (disposed || planeRef.current !== plane) {
            texture.dispose();
            return;
          }
          texture.colorSpace = 'srgb';
          // 各向异性过滤拉满：斜视角（画中画/轨道侧视）下纹理采样清晰度显著提升
          texture.anisotropy = rendererRef.current?.capabilities.getMaxAnisotropy() ?? 1;
          loadedTextureRef.current?.dispose();
          loadedTextureRef.current = texture;
          loadedTextureUrlRef.current = imageUrl;
          const image = texture.image as HTMLImageElement | undefined;
          // 首次加载成功回填缓存：面板重开/换回同图走同步命中路径
          if (image && image.complete && image.naturalWidth > 0) {
            imageCache.set(imageUrl, image);
            if (imageCache.size > IMAGE_CACHE_MAX) {
              const oldest = imageCache.keys().next().value;
              if (oldest !== undefined) imageCache.delete(oldest);
            }
          }
          const rawAspect = image && image.height ? image.width / image.height : 1;
          // 板宽按图片比例缓动（clamp 0.5~2.2，防极端比例溢出视口，对齐打灯视口）
          const aspect = Math.max(0.5, Math.min(2.2, rawAspect));
          startBoardTransition(texture, aspect);
          setTextureFailed(false);
        },
        undefined,
        () => {
          if (disposed) return;
          setTextureFailed(true);
          requestRender();
        }
      );
    });

    return () => {
      disposed = true;
      cancelAnimationFrame(boardRafId);
    };
  }, [ready, imageUrl, requestRender, updatePipFov]);

  // 拖拽期间 rAF 循环保证顺滑（含画中画实时刷新）
  const startRenderLoop = useCallback(() => {
    cancelAnimationFrame(rafRef.current);
    const loop = () => {
      renderScene();
      rafRef.current = requestAnimationFrame(loop);
    };
    rafRef.current = requestAnimationFrame(loop);
  }, [renderScene]);

  const stopRenderLoop = useCallback(() => {
    cancelAnimationFrame(rafRef.current);
    requestRender();
  }, [requestRender]);

  const stopInertia = useCallback(() => {
    cancelAnimationFrame(inertiaRafRef.current);
    inertiaRafRef.current = 0;
  }, []);

  // 右键释放后的惯性衰减：延续最后的轨道角速度按帧阻尼滑动，低于阈值自动停止
  const startOrbitInertia = useCallback(() => {
    stopInertia();
    const step = () => {
      const vel = orbitVelRef.current;
      if (Math.abs(vel.theta) < ORBIT_INERTIA_MIN && Math.abs(vel.phi) < ORBIT_INERTIA_MIN) {
        inertiaRafRef.current = 0;
        return;
      }
      orbitStateRef.current.theta -= vel.theta;
      orbitStateRef.current.phi = Math.max(
        ORBIT_PHI_MIN,
        Math.min(ORBIT_PHI_MAX, orbitStateRef.current.phi + vel.phi)
      );
      updateOrbitCamera();
      renderScene();
      vel.theta *= ORBIT_DAMPING;
      vel.phi *= ORBIT_DAMPING;
      inertiaRafRef.current = requestAnimationFrame(step);
    };
    inertiaRafRef.current = requestAnimationFrame(step);
  }, [stopInertia, updateOrbitCamera, renderScene]);

  // 左键命中测试（两段式）：先判指针是否按在点本体上（落在其投影球的屏幕半径内）直接命中该点，
  // 绿点与橙点屏幕重叠时不再被先检查的绿点抢占；都没有本体命中再退回「容差内最近」兜底
  const hitMarker = useCallback(
    (e: React.PointerEvent<HTMLDivElement>): 'azimuth' | 'elevation' | 'scale' | null => {
      const scratch = scratchVecRef.current;
      const camera = cameraRef.current;
      if (!scratch || !camera) return null;
      const { width, height } = sizeRef.current;
      const rect = e.currentTarget.getBoundingClientRect();
      const px = e.clientX - rect.left;
      const py = e.clientY - rect.top;
      const targets: Array<['azimuth' | 'elevation' | 'scale', import('three').Mesh | null]> = [
        ['azimuth', greenMarkerRef.current],
        ['elevation', pinkMarkerRef.current],
        ['scale', orangeMarkerRef.current],
      ];
      let best: 'azimuth' | 'elevation' | 'scale' | null = null;
      let bestDist = CLICK_TOLERANCE_PX;
      for (const [key, mesh] of targets) {
        if (!mesh) continue;
        mesh.getWorldPosition(scratch);
        // 变换到相机空间：z<0 才在相机前方；背后的点透视除法为负会翻转出错误屏幕位置，直接跳过
        scratch.applyMatrix4(camera.matrixWorldInverse);
        if (scratch.z >= 0) continue;
        const depth = -scratch.z;
        scratch.applyMatrix4(camera.projectionMatrix);
        const sx = ((scratch.x + 1) / 2) * width;
        const sy = ((1 - scratch.y) / 2) * height;
        const d = Math.hypot(sx - px, sy - py);
        // 第一段：按在点本体上（投影球屏幕半径 = 半径÷(深度·tan(半fov)) × 半屏高，设下限防远景点过小难点中）
        const radius = (mesh.geometry as import('three').SphereGeometry).parameters.radius;
        const screenR = ((radius / (depth * Math.tan((camera.fov * Math.PI) / 360))) * height) / 2;
        if (d <= Math.max(screenR, MARKER_BODY_HIT_PX)) return key;
        // 第二段：容差内最近者
        if (d <= bestDist) {
          bestDist = d;
          best = key;
        }
      }
      return best;
    },
    []
  );

  // 指针射线与绿环平面（y=RING_HEIGHT）求交，返回指针绕主体的方位角（度）：
  // 与绿点位置公式 (R·sinθ, h, R·cosθ) 同一坐标约定，拖拽时角度直接跟随鼠标而非位移增量
  const pointerAzimuthAngle = useCallback(
    (e: React.PointerEvent<HTMLDivElement>): number | null => {
      const camera = cameraRef.current;
      const scratch = scratchVecRef.current;
      if (!camera || !scratch) return null;
      const { width, height } = sizeRef.current;
      if (width === 0 || height === 0) return null;
      const rect = e.currentTarget.getBoundingClientRect();
      const nx = ((e.clientX - rect.left) / width) * 2 - 1;
      const ny = -(((e.clientY - rect.top) / height) * 2 - 1);
      scratch.set(nx, ny, 0.5).unproject(camera);
      const dirY = scratch.y - camera.position.y;
      // 射线朝上（指针在地平线以上）时与环平面无交点，跳过本次移动
      if (dirY >= -1e-6) return null;
      const t = (RING_HEIGHT - camera.position.y) / dirY;
      const x = camera.position.x + (scratch.x - camera.position.x) * t;
      const z = camera.position.z + (scratch.z - camera.position.z) * t;
      return (Math.atan2(x, z) * 180) / Math.PI;
    },
    []
  );

  // 指针射线与滑轨水平面（y=SCALE_LINE_Y）求交，把交点深度反解为连续浮点档位（0~4）：
  // 与绿点的绝对方位角跟随同构，橙点直接贴鼠标沿滑轨走而非按像素增量换算
  const pointerScaleIndex = useCallback(
    (e: React.PointerEvent<HTMLDivElement>): number | null => {
      const camera = cameraRef.current;
      const scratch = scratchVecRef.current;
      if (!camera || !scratch) return null;
      const { width, height } = sizeRef.current;
      if (width === 0 || height === 0) return null;
      const rect = e.currentTarget.getBoundingClientRect();
      const nx = ((e.clientX - rect.left) / width) * 2 - 1;
      const ny = -(((e.clientY - rect.top) / height) * 2 - 1);
      scratch.set(nx, ny, 0.5).unproject(camera);
      const dirY = scratch.y - camera.position.y;
      // 射线朝上（指针在地平线以上）时与滑轨平面无交点，保持当前档位
      if (dirY >= -1e-6) return null;
      const t = (SCALE_LINE_Y - camera.position.y) / dirY;
      const z = camera.position.z + (scratch.z - camera.position.z) * t;
      // 交点深度 clamp 到滑轨范围后反解档位：z=R_FAR→0（远景）、z=R_NEAR→4（特写）
      const dist = Math.min(R_FAR, Math.max(R_NEAR, z));
      return 4 * (1 - (dist - R_NEAR) / (R_FAR - R_NEAR));
    },
    []
  );

  // 指针射线与粉弧所在竖直平面求交，交点相对弧心的张角即弧上仰角（度）：
  // 与绿/橙点的绝对跟随同构，粉点直接贴鼠标沿弧走而非按像素增量换算
  const pointerElevationAngle = useCallback(
    (e: React.PointerEvent<HTMLDivElement>): number | null => {
      const camera = cameraRef.current;
      const scratch = scratchVecRef.current;
      if (!camera || !scratch) return null;
      const { width, height } = sizeRef.current;
      if (width === 0 || height === 0) return null;
      const rect = e.currentTarget.getBoundingClientRect();
      const nx = ((e.clientX - rect.left) / width) * 2 - 1;
      const ny = -(((e.clientY - rect.top) / height) * 2 - 1);
      scratch.set(nx, ny, 0.5).unproject(camera);
      // 弧平面由弧方位方向 d=(sinA,0,cosA) 与竖直轴张成，法线 n=(cosA,0,-sinA)
      const dirX = scratch.x - camera.position.x;
      const dirY = scratch.y - camera.position.y;
      const dirZ = scratch.z - camera.position.z;
      const nX = Math.cos(EL_ARC_AZIMUTH);
      const nZ = -Math.sin(EL_ARC_AZIMUTH);
      const denom = dirX * nX + dirZ * nZ;
      if (Math.abs(denom) < 1e-6) return null;
      const t = ((ARC_X - camera.position.x) * nX + (ARC_Z - camera.position.z) * nZ) / denom;
      if (t <= 0) return null;
      const hx = camera.position.x + dirX * t;
      const hy = camera.position.y + dirY * t;
      const hz = camera.position.z + dirZ * t;
      // 交点水平投影到弧方位方向得 h（=R·cos 仰角），竖直差为 R·sin 仰角，张角即仰角
      const h = (hx - ARC_X) * Math.sin(EL_ARC_AZIMUTH) + (hz - ARC_Z) * Math.cos(EL_ARC_AZIMUTH);
      return clampElevation((Math.atan2(hy - SUBJECT_Y, h) * 180) / Math.PI);
    },
    []
  );

  const handlePointerDown = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      // 任何按下先停掉进行中的轨道惯性，避免拖拽与惯性叠加
      stopInertia();
      orbitVelRef.current.theta = 0;
      orbitVelRef.current.phi = 0;
      // 右键/中键 → 环绕观察轨道（不影响角度参数）
      if (e.button === 2 || e.button === 1) {
        e.preventDefault();
        e.currentTarget.setPointerCapture(e.pointerId);
        dragPosRef.current = { x: e.clientX, y: e.clientY };
        orbitModeRef.current = true;
        setIsDragging(true);
        startRenderLoop();
        return;
      }
      // 左键：命中控制点才进入对应轴拖拽，未命中（空白）直接返回
      const hit = hitMarker(e);
      if (!hit) return;
      e.currentTarget.setPointerCapture(e.pointerId);
      dragPosRef.current = { x: e.clientX, y: e.clientY };
      dragTargetRef.current = hit;
      if (hit === 'azimuth') azDragAngleRef.current = azimuthRef.current;
      // 景别拖拽以当前整数档为浮点起点
      if (hit === 'scale') scaleDragFloatRef.current = scaleIndexRef.current;
      // 俯仰拖拽以当前整数度为浮点起点，上报基准同步，差值从零起算
      if (hit === 'elevation') {
        elDragFloatRef.current = elevationRef.current;
        elDragSentRef.current = elevationRef.current;
      }
      draggingRef.current = true;
      setIsDragging(true);
      // 进行中的过渡动画立即收尾到当前参数，避免拖拽起点卡在半途
      cancelMarkerAnimation();
      displayRef.current = { azimuth: azimuthRef.current, elevation: elevationRef.current, scaleIndex: scaleIndexRef.current };
      applyMarkerPositions(azimuthRef.current, elevationRef.current, scaleIndexRef.current);
      startRenderLoop();
    },
    [stopInertia, hitMarker, startRenderLoop, cancelMarkerAnimation, applyMarkerPositions]
  );

  const handlePointerMove = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      const pos = dragPosRef.current;
      if (!pos) {
        // 未按下：命中标记圆点则切换手掌光标，否则回到默认箭头
        setHoverMarker(!!hitMarker(e));
        return;
      }
      const dx = e.clientX - pos.x;
      const dy = e.clientY - pos.y;
      pos.x = e.clientX;
      pos.y = e.clientY;
      if (orbitModeRef.current) {
        const dTheta = dx * ORBIT_SENSITIVITY;
        const dPhi = dy * ORBIT_SENSITIVITY;
        orbitStateRef.current.theta -= dTheta;
        orbitStateRef.current.phi = Math.max(
          ORBIT_PHI_MIN,
          Math.min(ORBIT_PHI_MAX, orbitStateRef.current.phi + dPhi)
        );
        orbitVelRef.current.theta = dTheta;
        orbitVelRef.current.phi = dPhi;
        updateOrbitCamera();
        return;
      }
      // 按命中轴路由：绿点按指针绕主体的绝对角度跟随（点贴鼠标）、粉点沿弧贴鼠标、橙点上拖拉近（远景→特写）
      switch (dragTargetRef.current) {
        case 'azimuth': {
          const azDeg = pointerAzimuthAngle(e);
          if (azDeg !== null) {
            const delta = shortestAngleDelta(azDragAngleRef.current, azDeg);
            azDragAngleRef.current = azDeg;
            if (delta !== 0) onAzimuthChange(delta);
          }
          break;
        }
        case 'elevation': {
          // 绝对跟随：指针射线与粉弧平面求交反解弧上仰角，粉点实时贴鼠标沿弧滑动；
          // 整数度变化才上报父级差值（本地已先行渲染不依赖父级回流，两次 move 间不累积误差）
          const el = pointerElevationAngle(e);
          if (el !== null && el !== elDragFloatRef.current) {
            elDragFloatRef.current = el;
            displayRef.current.elevation = el;
            applyMarkerPositions(displayRef.current.azimuth, displayRef.current.elevation, displayRef.current.scaleIndex);
            renderScene();
            const stepped = clampElevation(Math.round(el));
            if (stepped !== elDragSentRef.current) {
              onElevationChange(stepped - elDragSentRef.current);
              elDragSentRef.current = stepped;
            }
          }
          break;
        }
        case 'scale': {
          // 绝对跟随：指针射线与滑轨平面求交反解浮点档位，橙点实时贴鼠标沿滑轨滑动；
          // 取整跨档才下发给父级（父级仍只收整数 index），松手吸附最近整数档
          const si = pointerScaleIndex(e);
          if (si !== null && si !== scaleDragFloatRef.current) {
            scaleDragFloatRef.current = si;
            displayRef.current.scaleIndex = si;
            applyMarkerPositions(displayRef.current.azimuth, displayRef.current.elevation, si);
            renderScene();
            const stepped = snapScaleIndex(si);
            if (stepped !== scaleIndexRef.current) onScaleIndexChange(stepped);
          }
          break;
        }
      }
    },
    [onAzimuthChange, onElevationChange, onScaleIndexChange, updateOrbitCamera, applyMarkerPositions, renderScene, pointerScaleIndex, pointerElevationAngle, hitMarker]
  );

  const endDrag = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      if (!dragPosRef.current) return;
      try {
        e.currentTarget.releasePointerCapture(e.pointerId);
      } catch {
        // pointer capture 可能已释放
      }
      const wasOrbit = orbitModeRef.current;
      // 景别拖拽松手：浮点档位吸附回最近三档（远景/中景/特写，拖拽期间父级只收到整数，显示最终以整数为准）
      if (dragTargetRef.current === 'scale') {
        const si = snapScaleIndex(scaleDragFloatRef.current);
        scaleDragFloatRef.current = si;
        displayRef.current.scaleIndex = si;
        applyMarkerPositions(displayRef.current.azimuth, displayRef.current.elevation, si);
        renderScene();
      }
      // 俯仰拖拽松手：浮点仰角吸附回最近整数度（与已上报值一致，显示与父级参数对齐）
      if (dragTargetRef.current === 'elevation') {
        const el = clampElevation(Math.round(elDragFloatRef.current));
        elDragFloatRef.current = el;
        displayRef.current.elevation = el;
        applyMarkerPositions(displayRef.current.azimuth, displayRef.current.elevation, displayRef.current.scaleIndex);
        renderScene();
      }
      dragPosRef.current = null;
      orbitModeRef.current = false;
      dragTargetRef.current = null;
      draggingRef.current = false;
      setIsDragging(false);
      stopRenderLoop();
      // 右键轨道释放：按最后角速度惯性滑动（左键拖点不产生惯性）
      if (wasOrbit) startOrbitInertia();
    },
    [stopRenderLoop, startOrbitInertia, applyMarkerPositions, renderScene]
  );

  // 滚轮 → 景别档位（需原生监听以 preventDefault，React onWheel 为 passive）
  useEffect(() => {
    const container = containerRef.current;
    if (!container || !ready) return;
    const onWheel = (e: WheelEvent) => {
      e.preventDefault();
      e.stopPropagation();
      const next = clampScaleIndex(scaleIndexRef.current + (e.deltaY < 0 ? 2 : -2));
      if (next !== scaleIndexRef.current) onScaleIndexChange(next);
    };
    container.addEventListener('wheel', onWheel, { passive: false });
    return () => container.removeEventListener('wheel', onWheel);
  }, [ready, onScaleIndexChange]);

  // 画布缩放 → InverseScaleBox 反向倍数随之变化，背衬分辨率跟随补足
  // （transform 变化不触发 ResizeObserver，与 InverseScaleBox 同源直订 ReactFlow store，不经过 React 重渲染）
  useEffect(() => {
    let prevZoom = store.getState().transform[2];
    const applyRatio = () => {
      const renderer = rendererRef.current;
      const el = containerRef.current;
      if (!ready || !renderer || !el) return;
      renderer.setPixelRatio(targetPixelRatio(el));
      requestRender();
    };
    applyRatio();
    const unsub = store.subscribe((state) => {
      const zoom = state.transform[2];
      if (zoom !== prevZoom) {
        prevZoom = zoom;
        applyRatio();
      }
    });
    return unsub;
  }, [ready, requestRender, store]);

  // 容器尺寸变化 → 更新相机与画布
  useEffect(() => {
    const container = containerRef.current;
    if (!container || !ready) return;
    const observer = new ResizeObserver(() => {
      const renderer = rendererRef.current;
      const camera = cameraRef.current;
      const width = container.clientWidth;
      const height = container.clientHeight;
      if (!renderer || !camera || width === 0 || height === 0) return;
      sizeRef.current = { width, height };
      renderer.setPixelRatio(targetPixelRatio(container));
      renderer.setSize(width, height);
      camera.aspect = width / height;
      camera.updateProjectionMatrix();
      requestRender();
    });
    observer.observe(container);
    return () => observer.disconnect();
  }, [ready, requestRender]);

  // 水平角度归一化到 0~359 显示（0° 左边即 359°），仅影响文案；内部角度与提示词生成仍用原值
  const azimuthDeg = ((azimuth % 360) + 360) % 360;
  const statusText =
    azimuthDeg === 0 && elevation === 0
      ? '相机未移动'
      : `水平 ${azimuthDeg}° · 俯仰 ${elevation}° · ${SCALE_LABELS[clampScaleIndex(scaleIndex)]}`;

  return (
    <div
      ref={containerRef}
      className="nodrag nowheel relative h-full w-full overflow-hidden rounded-lg select-none"
      style={{ background: '#0a0a0b', touchAction: 'none', cursor: isDragging ? 'grabbing' : hoverMarker ? 'grab' : 'default' }}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={endDrag}
      onPointerCancel={endDrag}
      onContextMenu={(e) => {
        e.preventDefault();
        e.stopPropagation();
      }}
    >
      <div ref={canvasHostRef} className="absolute inset-0" />

      {/* 图例卡（左上） */}
      <div className="pointer-events-none absolute left-3 top-3 rounded-xl border border-white/10 bg-black/50 px-3 py-2.5 text-[11px] leading-5 text-white/70 backdrop-blur-sm">
        <div className="mb-1 text-xs font-medium text-white/90">相机控制</div>
        <div className="flex items-center gap-2">
          <span className="inline-block h-2 w-2 rounded-full" style={{ background: '#2DD4A0' }} />
          水平旋转
        </div>
        <div className="flex items-center gap-2">
          <span className="inline-block h-2 w-2 rounded-full" style={{ background: '#EC5FA1' }} />
          垂直倾斜
        </div>
        <div className="flex items-center gap-2">
          <span className="inline-block h-2 w-2 rounded-full" style={{ background: '#F59E0B' }} />
          远近距离
        </div>
        <div className="mt-1 border-t border-white/10 pt-1 text-[10px] leading-4 text-white/40">右键拖动 · 环绕视角</div>
      </div>

      {/* 相机视角画中画（右上）：canvas 在卡片底层渲染图片区，卡片用 mask 镂空中间露出画面（实底背景会遮挡 canvas） */}
      <div
        className="pointer-events-none absolute rounded-xl border border-white/10 bg-[#1B1B1E]"
        style={{
          right: `${PIP_INSET_RATIO * 100}%`,
          top: `${PIP_INSET_RATIO * 100}%`,
          width: `${PIP_WIDTH_RATIO * 100}%`,
          padding: `${PIP_WIDTH_RATIO * PIP_PAD_RATIO * 100}%`,
          WebkitMask: 'linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0)',
          WebkitMaskComposite: 'xor',
          mask: 'linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0)',
          maskComposite: 'exclude',
        }}
      >
        <div style={{ aspectRatio: `${PIP_ASPECT}` }} />
      </div>
      {textureFailed && (
        <div
          className="pointer-events-none absolute rounded bg-black/60 p-0.5 text-[#F59E0B]"
          style={{
            right: `calc(${(PIP_INSET_RATIO + PIP_WIDTH_RATIO * PIP_PAD_RATIO) * 100}% + 2px)`,
            top: `calc(${(PIP_INSET_RATIO + PIP_WIDTH_RATIO * PIP_PAD_RATIO) * 100}% + 2px)`,
          }}
        >
          <ImageOff size={10} />
        </div>
      )}

      {/* 状态 pill（顶部居中：图例卡与画中画之间） */}
      <div className="pointer-events-none absolute top-3 left-1/2 -translate-x-1/2 whitespace-nowrap rounded-full border border-[#2DD4A0]/40 bg-[#161618]/90 px-4 py-1 text-[11px] font-medium text-[#2DD4A0] shadow-[0_0_16px_rgba(45,212,160,0.18)]">
        {statusText}
      </div>
    </div>
  );
});
